import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  InvoiceSortOption,
  InvoiceStatus,
  InvoiceType,
} from '@rematter/frontend-lib-api-client';

import { InvoiceRecord } from '../types';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

type Args = {
  searchTerm?: string;
  statuses?: InvoiceStatus[];
  types?: InvoiceType[];
  accountIDs?: string[];
  limit?: number;
  offset?: number;
  sorting: InvoiceSortOption[];
};

export const invoicesQueryAtom = createQueryAtomFactory(
  'finance.invoices',
  (params: Args) => ({
    invoices: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          statuses: params.statuses,
          types: params.types,
          accountIDs: params.accountIDs,
        },
        pagination: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 10,
        },
        sorting: params.sorting,
      },
      entries: {
        id: true,
        type: true,
        documentNumber: true,
        documentDate: true,
        dueDate: true,
        status: true,
        notes: true,
        paymentTerm: true,
        account: {
          id: true,
          name: true,
          type: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
        amount: {
          amount: true,
          currencyCode: true,
        },
        openAmount: {
          amount: true,
          currencyCode: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { invoices: InvoiceRecord[]; total: number } => {
      const entries = data?.invoices.entries;
      if (!entries) {
        return { invoices: [], total: 0 };
      }

      return {
        invoices: entries?.map((it) => ({
          id: it.id,
          type: it.type,
          documentNumber: it.documentNumber,
          documentDate: maybeParseCalendarDate(it.documentDate) ?? null,
          dueDate: maybeParseCalendarDate(it.dueDate) ?? null,
          accountId: it.account?.id ?? null,
          accountName: it.account?.name ?? '',
          accountType: it.account?.type,
          facilityId: it.facility?.id ?? '',
          facilityName: it.facility?.facilityName ?? '',
          amount: toMoney(it.amount),
          openAmount: toMoney(it.openAmount),
          status: it.status,
          notes: it.notes ?? null,
          paymentTerm: it.paymentTerm ?? null,
          lineItems: [],
          payments: [],
          timestamps: {
            createdAt: parseZonedDateTime(it.timestamps.createdAt),
            updatedAt: parseZonedDateTime(it.timestamps.updatedAt),
          },
        })),
        total: data?.invoices.page.total ?? 0,
      };
    },
  },
);
